<template>
  <div>
    <!-- ─── Desktop Sidebar (≥960px) ─── -->
    <v-navigation-drawer
      v-if="!isMobile"
      class="seller-drawer"
      permanent
      app
      touchless
      :width="isMini ? 80 : 280"
      :mini-variant="isMini"
    >
      <div class="header-container">
        <div v-if="!isMini" class="logo-wrapper">
          <div class="logo-text">ELSONIII</div>
          <div class="logo-subtitle">ร้านค้า</div>
        </div>
        <div class="header-actions">
          <v-btn icon class="hamburger-btn" @click="isMini = !isMini">
            <v-icon color="#F4811F" size="24">mdi-menu</v-icon>
          </v-btn>
        </div>
      </div>

      <v-list nav dense class="menu-list">
        <v-list-item
          v-for="item in menus"
          :key="item.to"
          :to="item.to"
          router link
          class="menu-item"
          :class="{ 'menu-item--active': isActive(item), 'menu-item--mini': isMini }"
        >
          <v-list-item-icon class="menu-icon">
            <v-badge
              :value="isMini && badgeCount(item.to) > 0"
              :content="badgeCount(item.to) > 99 ? '99+' : String(badgeCount(item.to))"
              color="#FF5B5B"
              overlap
              offset-x="6"
              offset-y="6"
            >
              <v-icon>{{ item.icon }}</v-icon>
            </v-badge>
          </v-list-item-icon>
          <v-list-item-content v-if="!isMini">
            <v-list-item-title class="menu-title">{{ item.label }}</v-list-item-title>
          </v-list-item-content>
          <v-chip v-if="!isMini && badgeCount(item.to) > 0"
            x-small color="#FF5B5B" text-color="white" class="font-weight-bold ml-auto px-2">
            {{ badgeCount(item.to) > 99 ? '99+' : badgeCount(item.to) }}
          </v-chip>
        </v-list-item>
      </v-list>

      <template v-slot:append>
        <div v-if="!isMini" class="drawer-footer">
          <v-list dense class="pa-0">
            <v-list-item class="footer-item footer-profile">
              <v-avatar size="44" class="mr-3" rounded="lg">
                <img v-if="hasAvatar" :src="userAvatar" alt="avatar" @error="onAvatarError" />
                <v-avatar v-else size="44" color="#FEF3ED" rounded="lg">
                  <v-icon size="24" color="#F4811F">mdi-account-circle</v-icon>
                </v-avatar>
              </v-avatar>
              <v-list-item-content>
                <div class="footer-item-title">{{ userName }}</div>
                <div class="footer-item-subtitle">ผู้ขาย</div>
              </v-list-item-content>
              <v-btn icon small class="ml-2" @click="$emit('logout')">
                <v-icon size="22" color="#ef4444">mdi-logout</v-icon>
              </v-btn>
            </v-list-item>
          </v-list>
        </div>
        <div v-else class="drawer-footer-mini">
          <v-avatar size="40" class="mb-3" rounded="lg">
            <img v-if="hasAvatar" :src="userAvatar" alt="avatar" @error="onAvatarError" />
            <v-avatar v-else size="40" color="#FEF3ED" rounded="lg">
              <v-icon size="24" color="#F4811F">mdi-account-circle</v-icon>
            </v-avatar>
          </v-avatar>
          <v-btn icon @click="$emit('logout')">
            <v-icon color="#ef4444" size="24">mdi-logout</v-icon>
          </v-btn>
        </div>
      </template>
    </v-navigation-drawer>

    <!-- ─── Mobile Bottom Nav Bar ─── -->
    <div v-if="isMobile" class="sbn-wrapper">
      <nav class="sbn">
        <router-link
          v-for="item in bottomMenus"
          :key="item.to"
          :to="item.to"
          class="sbn-item"
          :class="{ 'sbn-item--active': isActive(item) }"
        >
          <div class="sbn-icon-wrap" style="position:relative">
            <v-icon size="22">{{ item.icon }}</v-icon>
            <span v-if="badgeCount(item.to) > 0" class="sbn-badge">
              {{ badgeCount(item.to) > 99 ? '99+' : badgeCount(item.to) }}
            </span>
          </div>
          <span class="sbn-label">{{ item.label }}</span>
        </router-link>

        <button class="sbn-item" @click="mobileDrawerOpen = true">
          <div class="sbn-icon-wrap">
            <v-icon size="22">mdi-dots-horizontal-circle-outline</v-icon>
          </div>
          <span class="sbn-label">เพิ่มเติม</span>
        </button>
      </nav>
    </div>

    <!-- ─── Mobile Bottom Sheet (More) ─── -->
    <v-bottom-sheet v-if="isMobile" v-model="mobileDrawerOpen" scrollable>
      <v-sheet class="sbn-sheet">
        <div class="sbn-sheet-handle"></div>

        <div class="sbn-sheet-profile">
          <v-avatar size="48" rounded="lg">
            <img v-if="hasAvatar" :src="userAvatar" alt="avatar" @error="onAvatarError" />
            <v-avatar v-else size="48" color="#FEF3ED" rounded="lg">
              <v-icon size="28" color="#F4811F">mdi-account-circle</v-icon>
            </v-avatar>
          </v-avatar>
          <div class="sbn-sheet-profile-info">
            <div class="sbn-sheet-name">{{ userName }}</div>
            <div class="sbn-sheet-sub">ผู้ขาย</div>
          </div>
          <v-btn icon @click="mobileDrawerOpen = false">
            <v-icon color="#9CA3AF">mdi-close</v-icon>
          </v-btn>
        </div>

        <v-divider class="mb-4"></v-divider>

        <div class="sbn-sheet-grid">
          <router-link
            v-for="item in extraMenus"
            :key="item.to"
            :to="item.to"
            class="sbn-sheet-item"
            :class="{ 'sbn-sheet-item--active': isActive(item) }"
            @click.native="mobileDrawerOpen = false"
          >
            <div class="sbn-sheet-icon" style="position:relative">
              <v-icon size="24">{{ item.icon }}</v-icon>
              <span v-if="badgeCount(item.to) > 0" class="sbn-badge">
                {{ badgeCount(item.to) > 99 ? '99+' : badgeCount(item.to) }}
              </span>
            </div>
            <span class="sbn-sheet-label">{{ item.label }}</span>
          </router-link>
        </div>

        <div class="sbn-sheet-logout">
          <v-btn block outlined color="error" rounded @click="$emit('logout')" class="logout-btn">
            <v-icon left>mdi-logout</v-icon>ออกจากระบบ
          </v-btn>
        </div>
      </v-sheet>
    </v-bottom-sheet>
  </div>
</template>

<script>
import { getSellerProfileApi } from "@/services/api/auth";

export default {
  name: "SidebarSeller",

  props: {
    notificationCount: { type: Number, default: 0 },
  },

  data() {
    return {
      isMini: false,
      avatarError: false,
      adminProfile: null,
      mobileDrawerOpen: false,
      menus: [
        { label: "แดชบอร์ด",          to: "/seller/dashboard",       icon: "mdi-view-dashboard-outline" },
        { label: "จัดการสินค้า",       to: "/seller/products",        icon: "mdi-package-variant-closed" },
        { label: "จัดการคำสั่งซื้อ",   to: "/seller/orders",          icon: "mdi-receipt-text-outline" },
        { label: "ตั้งค่าการจัดส่ง",   to: "/seller/shipping-config", icon: "mdi-truck-outline" },
        { label: "กระเป๋าเงิน",        to: "/seller/wallet",          icon: "mdi-wallet-outline" },
        { label: "แจ้งปัญหา / Ticket", to: "/seller/tickets",         icon: "mdi-ticket-outline" },
        { label: "แชทกับลูกค้า",       to: "/seller/chat",            icon: "mdi-chat-processing-outline" },
        { label: "โปรไฟล์ของฉัน",      to: "/seller/profile",         icon: "mdi-account-circle-outline" },
      ],
      bottomMenus: [
        { label: "แดชบอร์ด", to: "/seller/dashboard", icon: "mdi-view-dashboard-outline" },
        { label: "สินค้า",    to: "/seller/products",  icon: "mdi-package-variant-closed" },
        { label: "ออเดอร์",   to: "/seller/orders",    icon: "mdi-receipt-text-outline" },
        { label: "กระเป๋า",   to: "/seller/wallet",    icon: "mdi-wallet-outline" },
      ],
      extraMenus: [
        { label: "ตั้งค่าจัดส่ง", to: "/seller/shipping-config", icon: "mdi-truck-outline" },
        { label: "แจ้งปัญหา",     to: "/seller/tickets",         icon: "mdi-ticket-outline" },
        { label: "แชท",           to: "/seller/chat",            icon: "mdi-chat-processing-outline" },
        { label: "โปรไฟล์",       to: "/seller/profile",         icon: "mdi-account-circle-outline" },
      ],
    };
  },

  computed: {
    isMobile() {
      return this.$vuetify.breakpoint.smAndDown;
    },
    badgeCount() {
      return (to) => {
        if (to === "/seller/chat")   return this.$store.getters.chatBadge;
        if (to === "/seller/orders") return this.$store.getters.orderBadge;
        return 0;
      };
    },
    userName() {
      if (!this.adminProfile) return "ชื่อผู้ใช้";
      return this.adminProfile.shopName || this.adminProfile.username || "ชื่อผู้ใช้";
    },
    userAvatar() {
      const img = this.adminProfile?.profileImage || this.adminProfile?.storeImage || "";
      if (!img) return "";
      return img.startsWith("http") ? img : `${this.$domain_api}${img}`;
    },
    hasAvatar() {
      return !!this.userAvatar && !this.avatarError;
    },
  },

  async created() {
    try {
      const data = await getSellerProfileApi();
      this.adminProfile = data.seller || data;
    } catch (e) {
      console.error("[SidebarSeller] load profile failed:", e);
    }
  },

  methods: {
    isActive(item) {
      return this.$route.path.startsWith(item.to);
    },
    onAvatarError() {
      this.avatarError = true;
    },
  },
};
</script>

<style scoped>
/* ─── Desktop Sidebar ─── */
.seller-drawer ::v-deep(.v-navigation-drawer__border) { display: none !important; }

.header-container { position: relative; padding: 20px 0 16px; }
.logo-wrapper { display: flex; flex-direction: column; align-items: center; gap: 2px; margin-top: 8px; margin-bottom: 8px; }
.logo-text { font-size: 23px; font-weight: 700; letter-spacing: 0.1em; color: #F4811F; padding-top: 30px; }
.logo-subtitle { font-size: 13px; font-weight: 600; color: #6b7280; }
.header-actions { position: absolute; top: 6px; right: 12px; display: flex; align-items: center; gap: 4px; }

.menu-list { padding-top: 10px; }
.menu-item {
  position: relative; border-radius: 8px; margin: 6px 10px;
  padding-left: 8px; padding-right: 10px; min-height: 42px;
  color: #4b5563; transition: 0.2s;
}
.menu-item:hover { background: #FEF3ED; }
.menu-item--active { background: #F4811F !important; color: white !important; }
::v-deep .menu-item.menu-item--mini.menu-item--active {
  width: 40px; height: 40px; margin: 4px auto; padding: 0;
  border-radius: 10px; display: flex; justify-content: center; align-items: center;
}
.menu-icon { margin-right: 10px !important; }
.menu-title { font-size: 15px !important; font-weight: 600; }
.menu-item--mini { justify-content: center; padding-left: 0; padding-right: 0; }
.menu-item--mini .menu-icon { margin-right: 0 !important; justify-content: center; }

.drawer-footer { padding: 8px 6px 10px; }
.drawer-footer-mini { padding: 8px 0 10px; display: flex; flex-direction: column; align-items: center; }
.footer-item { min-height: 60px !important; margin: 0 10px !important; border-radius: 12px !important; transition: all 0.2s; border: 1px solid transparent; }
.footer-profile { background: #fafafa; border: 1px solid #f1f5f9; margin-top: 10px !important; }
.footer-profile:hover { background: #FEF3ED; border-color: #FBCFB8; }
.footer-item-title { font-size: 14px; font-weight: 700; color: #1e293b; line-height: 1.2; }
.footer-item-subtitle { font-size: 12px; color: #6b7280; margin-top: 2px; }

/* ─── Mobile Bottom Nav ─── */
.sbn-wrapper {
  position: fixed;
  bottom: 0; left: 0; right: 0;
  z-index: 200;
}

.sbn {
  display: flex;
  align-items: stretch;
  background: white;
  border-top: 1px solid #F3F4F6;
  box-shadow: 0 -2px 16px rgba(0,0,0,0.07);
  padding-bottom: env(safe-area-inset-bottom, 0px);
}

.sbn-item {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 3px;
  padding: 8px 4px 6px;
  text-decoration: none;
  color: #9CA3AF;
  background: none;
  border: none;
  cursor: pointer;
  outline: none;
  font-family: 'Kanit', 'Noto Sans Thai', sans-serif;
  transition: color 0.2s;
  -webkit-tap-highlight-color: transparent;
  min-height: 56px;
}

.sbn-item--active { color: #F4811F; }

.sbn-icon-wrap {
  width: 36px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  transition: background 0.2s, transform 0.15s;
}

.sbn-item--active .sbn-icon-wrap {
  background: #FEF3ED;
  transform: translateY(-1px);
}

.sbn-label {
  font-size: 10px;
  font-weight: 600;
  line-height: 1;
  white-space: nowrap;
}

/* ─── Mobile Bottom Sheet ─── */
.sbn-sheet {
  border-radius: 24px 24px 0 0 !important;
  padding: 0 16px calc(20px + env(safe-area-inset-bottom, 0px));
  font-family: 'Kanit', 'Noto Sans Thai', sans-serif;
  max-height: 70vh;
  overflow-y: auto;
}

.sbn-sheet-handle {
  width: 40px; height: 4px;
  background: #E5E7EB; border-radius: 4px;
  margin: 12px auto 16px;
}

.sbn-sheet-profile {
  display: flex; align-items: center; gap: 12px; padding: 4px 0 16px;
}
.sbn-sheet-profile-info { flex: 1; min-width: 0; }
.sbn-sheet-name { font-size: 16px; font-weight: 700; color: #111827; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.sbn-sheet-sub  { font-size: 12px; color: #6B7280; margin-top: 2px; }

.sbn-sheet-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
  margin-bottom: 20px;
}

.sbn-sheet-item {
  display: flex; flex-direction: column; align-items: center; gap: 8px;
  padding: 14px 8px; border-radius: 16px; background: #F9FAFB;
  text-decoration: none; color: #374151;
  transition: background 0.15s, transform 0.15s;
  -webkit-tap-highlight-color: transparent;
}
.sbn-sheet-item:active { transform: scale(0.96); }
.sbn-sheet-item--active { background: #FEF3ED; color: #D96E10; }

.sbn-sheet-icon {
  width: 44px; height: 44px; border-radius: 12px;
  background: white; display: flex; align-items: center; justify-content: center;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
}
.sbn-sheet-item--active .sbn-sheet-icon { background: #FEF3ED; }

.sbn-sheet-label { font-size: 12px; font-weight: 600; text-align: center; line-height: 1.2; }

.sbn-badge {
  position: absolute;
  top: -6px;
  right: -8px;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  border-radius: 8px;
  background: #FF5B5B;
  color: white;
  font-size: 9px;
  font-weight: 700;
  line-height: 16px;
  text-align: center;
  white-space: nowrap;
}

.sbn-sheet-logout { padding: 0 4px; }
.logout-btn {
  height: 48px !important; font-size: 14px !important; font-weight: 700 !important;
  text-transform: none !important; border-radius: 14px !important; letter-spacing: 0 !important;
  font-family: 'Kanit', 'Noto Sans Thai', sans-serif !important;
}
</style>
